import React, { useEffect, useState } from 'react';
import { homepageApi, categoriesApi, shopsApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { ImageUpload } from '@/components/ui/ImageUpload';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Category, Shop } from '@/types';

interface HomepageBanner {
  id: number;
  title: string;
  subtitle?: string;
  image_url?: string;
  button_text: string;
  button_link: string;
  bg_color: string;
  text_color: string;
  is_active: boolean;
  display_order: number;
}

interface HomepageSection {
  id: number;
  title: string;
  subtitle?: string;
  section_type: string;
  category_id?: number;
  product_type?: string;
  limit: number;
  is_active: boolean;
  display_order: number;
}

interface HomepageCategoryGroup {
  id: number;
  title: string;
  category_ids: number[];
  display_limit: number;
  is_active: boolean;
  display_order: number;
}

interface HomepagePromoCard {
  id: number;
  title: string;
  subtitle?: string;
  image_url?: string;
  button_text: string;
  button_link: string;
  bg_color: string;
  text_color: string;
  is_active: boolean;
  display_order: number;
}

const HomepageManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'banners' | 'sections' | 'categories' | 'promos'>('banners');
  const [banners, setBanners] = useState<HomepageBanner[]>([]);
  const [sections, setSections] = useState<HomepageSection[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<HomepageCategoryGroup[]>([]);
  const [promoCards, setPromoCards] = useState<HomepagePromoCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [modalType, setModalType] = useState<'banner' | 'section' | 'category' | 'promo'>('banner');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [imageMode, setImageMode] = useState<'url' | 'upload'>('url');
  const [imageFile, setImageFile] = useState<File | null>(null);

  // For searchable dropdowns
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allShops, setAllShops] = useState<Shop[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [shopSearch, setShopSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Fetch categories and shops when modal opens for sections
  useEffect(() => {
    if (showModal && modalType === 'sections') {
      fetchCategoriesAndShops();
    }
  }, [showModal, modalType]);

  const fetchCategoriesAndShops = async () => {
    try {
      const [categoriesResponse, shopsResponse] = await Promise.all([
        categoriesApi.getAll(),
        shopsApi.getAll()
      ]);
      setAllCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
      setAllShops(Array.isArray(shopsResponse.data) ? shopsResponse.data : (shopsResponse.data?.items || []));
    } catch (error) {
      console.error('Error fetching categories and shops:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'banners') {
        const response = await homepageApi.getBanners();
        console.log('Banners response:', response);
        console.log('Banners data:', response.data);
        setBanners(Array.isArray(response.data) ? response.data : []);
      } else if (activeTab === 'sections') {
        const response = await homepageApi.getSections();
        console.log('Sections response:', response);
        console.log('Sections data:', response.data);
        setSections(Array.isArray(response.data) ? response.data : []);
      } else if (activeTab === 'categories') {
        const response = await homepageApi.getCategoryGroups();
        console.log('Category Groups response:', response);
        console.log('Category Groups data:', response.data);
        setCategoryGroups(Array.isArray(response.data) ? response.data : []);
      } else if (activeTab === 'promos') {
        const response = await homepageApi.getPromoCards();
        console.log('Promo Cards response:', response);
        console.log('Promo Cards data:', response.data);
        setPromoCards(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Set empty arrays on error to show "No items found"
      if (activeTab === 'banners') setBanners([]);
      else if (activeTab === 'sections') setSections([]);
      else if (activeTab === 'categories') setCategoryGroups([]);
      else if (activeTab === 'promos') setPromoCards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setModalType(activeTab);
    setImageMode('url');
    setImageFile(null);
    setCategorySearch('');
    setShopSearch('');

    // Initialize form data based on type
    const initialData: any = { is_active: true, display_order: 1 };
    if (activeTab === 'banners' || activeTab === 'promos') {
      initialData.title = '';
      initialData.subtitle = '';
      initialData.button_text = '';
      initialData.button_link = '';
      initialData.bg_color = '#3b82f6';
      initialData.text_color = '#ffffff';
      initialData.bg_opacity = 0.6; // Default 60% opacity
    } else if (activeTab === 'sections') {
      initialData.title = '';
      initialData.subtitle = '';
      initialData.section_type = 'featured';
      initialData.limit = 12;
    } else if (activeTab === 'categories') {
      initialData.title = '';
      initialData.category_ids = [];
      initialData.display_limit = 8;
    }

    setFormData(initialData);
    setShowModal(true);
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setModalType(activeTab);
    setFormData({ ...item, bg_opacity: item.bg_opacity ?? 0.6 }); // Default opacity if not set
    setImageMode('url');
    setImageFile(null);
    setCategorySearch('');
    setShopSearch('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCategorySearch('');
    setShopSearch('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let savedItem: any = null;

      if (activeTab === 'banners') {
        if (editingItem) {
          const response = await homepageApi.updateBanner(editingItem.id, formData);
          savedItem = response.data;
        } else {
          const response = await homepageApi.createBanner(formData);
          savedItem = response.data;
        }

        // Handle image upload if file is selected
        if (imageMode === 'upload' && imageFile && savedItem) {
          await homepageApi.uploadBannerImage(savedItem.id, imageFile);
        }
      } else if (activeTab === 'sections') {
        if (editingItem) {
          await homepageApi.updateSection(editingItem.id, formData);
        } else {
          await homepageApi.createSection(formData);
        }
      } else if (activeTab === 'categories') {
        if (editingItem) {
          await homepageApi.updateCategoryGroup(editingItem.id, formData);
        } else {
          await homepageApi.createCategoryGroup(formData);
        }
      } else if (activeTab === 'promos') {
        let savedPromo: any = null;
        if (editingItem) {
          const response = await homepageApi.updatePromoCard(editingItem.id, formData);
          savedPromo = response.data;
        } else {
          const response = await homepageApi.createPromoCard(formData);
          savedPromo = response.data;
        }

        // Handle image upload if file is selected
        if (imageMode === 'upload' && imageFile && savedPromo) {
          await homepageApi.uploadPromoImage(savedPromo.id, imageFile);
        }
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      if (activeTab === 'banners') {
        await homepageApi.deleteBanner(id);
        setBanners(banners.filter(b => b.id !== id));
      } else if (activeTab === 'sections') {
        await homepageApi.deleteSection(id);
        setSections(sections.filter(s => s.id !== id));
      } else if (activeTab === 'categories') {
        await homepageApi.deleteCategoryGroup(id);
        setCategoryGroups(categoryGroups.filter(cg => cg.id !== id));
      } else if (activeTab === 'promos') {
        await homepageApi.deletePromoCard(id);
        setPromoCards(promoCards.filter(pc => pc.id !== id));
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete item');
    }
  };

  const handleToggleActive = async (item: any) => {
    try {
      if (activeTab === 'banners') {
        await homepageApi.updateBanner(item.id, { is_active: !item.is_active });
        setBanners(banners.map(b => b.id === item.id ? { ...b, is_active: !b.is_active } : b));
      } else if (activeTab === 'sections') {
        await homepageApi.updateSection(item.id, { is_active: !item.is_active });
        setSections(sections.map(s => s.id === item.id ? { ...s, is_active: !s.is_active } : s));
      } else if (activeTab === 'categories') {
        await homepageApi.updateCategoryGroup(item.id, { is_active: !item.is_active });
        setCategoryGroups(categoryGroups.map(cg => cg.id === item.id ? { ...cg, is_active: !cg.is_active } : cg));
      } else if (activeTab === 'promos') {
        await homepageApi.updatePromoCard(item.id, { is_active: !item.is_active });
        setPromoCards(promoCards.map(pc => pc.id === item.id ? { ...pc, is_active: !pc.is_active } : pc));
      }
    } catch (error) {
      console.error('Error toggling active:', error);
      alert('Failed to update item');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Homepage Content Management</h1>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />
          Add New
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'banners', label: 'Banners' },
            { id: 'sections', label: 'Sections' },
            { id: 'categories', label: 'Category Groups' },
            { id: 'promos', label: 'Promo Cards' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white shadow rounded-lg">
        {activeTab === 'banners' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Button Text</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {banners.map((banner) => (
                  <tr key={banner.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{banner.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{banner.button_text}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{banner.display_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(banner)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          banner.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {banner.is_active ? <EyeIcon className="h-4 w-4 mr-1" /> : <EyeSlashIcon className="h-4 w-4 mr-1" />}
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(banner)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(banner.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'sections' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Limit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sections.map((section) => (
                  <tr key={section.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{section.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{section.section_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{section.limit}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{section.display_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(section)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          section.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {section.is_active ? <EyeIcon className="h-4 w-4 mr-1" /> : <EyeSlashIcon className="h-4 w-4 mr-1" />}
                        {section.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(section)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(section.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categories</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Limit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categoryGroups.map((group) => (
                  <tr key={group.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{group.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.category_ids.length} categories</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.display_limit}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.display_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(group)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          group.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {group.is_active ? <EyeIcon className="h-4 w-4 mr-1" /> : <EyeSlashIcon className="h-4 w-4 mr-1" />}
                        {group.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(group)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(group.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'promos' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Button Text</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {promoCards.map((card) => (
                  <tr key={card.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{card.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{card.button_text}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{card.display_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(card)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          card.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {card.is_active ? <EyeIcon className="h-4 w-4 mr-1" /> : <EyeSlashIcon className="h-4 w-4 mr-1" />}
                        {card.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(card)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(card.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {((activeTab === 'banners' && banners.length === 0) ||
          (activeTab === 'sections' && sections.length === 0) ||
          (activeTab === 'categories' && categoryGroups.length === 0) ||
          (activeTab === 'promos' && promoCards.length === 0)) && (
          <div className="text-center py-12">
            <p className="text-gray-500">No items found. Click "Add New" to create one.</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        size="lg"
      >
        <ModalHeader>
          {editingItem ? 'Edit' : 'Create'}{' '}
          {modalType === 'banners' ? 'Banner' :
           modalType === 'sections' ? 'Section' :
           modalType === 'categories' ? 'Category Group' :
           'Promo Card'}
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            {/* Common fields for banners and promos */}
            {(modalType === 'banners' || modalType === 'promos') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle || ''}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {/* Image Input - URL or Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Image
                  </label>
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setImageMode('url')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        imageMode === 'url'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Image URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageMode('upload')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        imageMode === 'upload'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Upload Image
                    </button>
                  </div>

                  {imageMode === 'url' ? (
                    <input
                      type="text"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  ) : (
                    <ImageUpload
                      value={formData.image_url}
                      onChange={(file) => setImageFile(file)}
                      label=""
                      helperText="Upload a banner image (max 5MB)"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text *
                  </label>
                  <input
                    type="text"
                    value={formData.button_text || ''}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Link *
                  </label>
                  <input
                    type="text"
                    value={formData.button_link || ''}
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="/products"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Background Color
                    </label>
                    <input
                      type="color"
                      value={formData.bg_color || '#3b82f6'}
                      onChange={(e) => setFormData({ ...formData, bg_color: e.target.value })}
                      className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Text Color
                    </label>
                    <input
                      type="color"
                      value={formData.text_color || '#ffffff'}
                      onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                      className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Overlay Opacity: {Math.round((formData.bg_opacity ?? 0.6) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={formData.bg_opacity ?? 0.6}
                    onChange={(e) => setFormData({ ...formData, bg_opacity: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Transparent (Image visible)</span>
                    <span>Opaque (Color visible)</span>
                  </div>
                </div>
              </>
            )}

            {/* Section fields */}
            {modalType === 'sections' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle || ''}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Section Type *
                  </label>
                  <select
                    value={formData.section_type || 'featured'}
                    onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="featured">Featured</option>
                    <option value="trending">Trending</option>
                    <option value="new_arrivals">New Arrivals</option>
                    <option value="best_sellers">Best Sellers</option>
                    <option value="deals">Deals</option>
                    <option value="category">Category</option>
                  </select>
                </div>

                {/* Show Category selector if section type is 'category' */}
                {formData.section_type === 'category' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Category *
                    </label>
                    <select
                      value={formData.category_id || ''}
                      onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Select a category --</option>
                      {allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name} (ID: {cat.id})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Select the category to display products from</p>
                  </div>
                )}

                {/* Advanced Filters Section */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Advanced Filters</h4>

                  {/* Multiple Categories */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Multiple Categories
                    </label>

                    {/* Selected Categories */}
                    {formData.category_ids && formData.category_ids.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.category_ids.map((catId: number) => {
                          const category = allCategories.find(c => c.id === catId);
                          return (
                            <span
                              key={catId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm"
                            >
                              {category?.name || `ID: ${catId}`}
                              <button
                                type="button"
                                onClick={() => {
                                  const newIds = formData.category_ids.filter((id: number) => id !== catId);
                                  setFormData({ ...formData, category_ids: newIds.length > 0 ? newIds : undefined });
                                }}
                                className="hover:bg-blue-200 rounded-full p-0.5"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Category Search/Select */}
                    <div className="relative">
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        placeholder="Search categories..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {categorySearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {allCategories
                            .filter(cat =>
                              cat.name.toLowerCase().includes(categorySearch.toLowerCase()) &&
                              !(formData.category_ids || []).includes(cat.id)
                            )
                            .map(category => (
                              <button
                                key={category.id}
                                type="button"
                                onClick={() => {
                                  const currentIds = formData.category_ids || [];
                                  setFormData({ ...formData, category_ids: [...currentIds, category.id] });
                                  setCategorySearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 flex justify-between items-center"
                              >
                                <span>{category.name}</span>
                                <span className="text-xs text-gray-500">ID: {category.id}</span>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Search and select multiple categories</p>
                  </div>

                  {/* Product Type Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Type
                    </label>
                    <select
                      value={formData.product_type || ''}
                      onChange={(e) => setFormData({ ...formData, product_type: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Products</option>
                      <option value="brand_new">Brand New</option>
                      <option value="recycled">Recycled</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Filter products by type (optional)</p>
                  </div>

                  {/* Shop/Brand Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specific Shops/Brands
                    </label>

                    {/* Selected Shops */}
                    {formData.shop_ids && formData.shop_ids.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.shop_ids.map((shopId: number) => {
                          const shop = allShops.find(s => s.id === shopId);
                          return (
                            <span
                              key={shopId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-md text-sm"
                            >
                              {shop?.name || `ID: ${shopId}`}
                              <button
                                type="button"
                                onClick={() => {
                                  const newIds = formData.shop_ids.filter((id: number) => id !== shopId);
                                  setFormData({ ...formData, shop_ids: newIds.length > 0 ? newIds : undefined });
                                }}
                                className="hover:bg-green-200 rounded-full p-0.5"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Shop Search/Select */}
                    <div className="relative">
                      <input
                        type="text"
                        value={shopSearch}
                        onChange={(e) => setShopSearch(e.target.value)}
                        placeholder="Search shops/brands..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {shopSearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {allShops
                            .filter(shop =>
                              shop.name.toLowerCase().includes(shopSearch.toLowerCase()) &&
                              !(formData.shop_ids || []).includes(shop.id)
                            )
                            .map(shop => (
                              <button
                                key={shop.id}
                                type="button"
                                onClick={() => {
                                  const currentIds = formData.shop_ids || [];
                                  setFormData({ ...formData, shop_ids: [...currentIds, shop.id] });
                                  setShopSearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 flex justify-between items-center"
                              >
                                <span>{shop.name}</span>
                                <span className="text-xs text-gray-500">ID: {shop.id}</span>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Search and select specific shops/brands</p>
                  </div>

                  {/* Minimum Discount Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Discount (%)
                    </label>
                    <input
                      type="number"
                      value={formData.min_discount || ''}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setFormData({ ...formData, min_discount: !isNaN(value) ? value : undefined });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 20"
                      min="0"
                      max="100"
                    />
                    <p className="mt-1 text-xs text-gray-500">Show only products with at least this discount percentage</p>
                  </div>

                  {/* Sort By */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sort By
                    </label>
                    <select
                      value={formData.sort_by || ''}
                      onChange={(e) => setFormData({ ...formData, sort_by: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Default (Featured/Random)</option>
                      <option value="price_asc">Price: Low to High</option>
                      <option value="price_desc">Price: High to Low</option>
                      <option value="newest">Newest First</option>
                      <option value="popular">Most Popular</option>
                      <option value="discount">Highest Discount</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">How to sort the products in this section</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Limit
                  </label>
                  <input
                    type="number"
                    value={formData.limit || 12}
                    onChange={(e) => setFormData({ ...formData, limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="50"
                  />
                </div>
              </>
            )}

            {/* Category Group fields */}
            {modalType === 'categories' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category IDs (comma-separated) *
                  </label>
                  <input
                    type="text"
                    value={Array.isArray(formData.category_ids) ? formData.category_ids.join(',') : ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      category_ids: e.target.value.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id))
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="1,2,3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Limit
                  </label>
                  <input
                    type="number"
                    value={formData.display_limit || 8}
                    onChange={(e) => setFormData({ ...formData, display_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="20"
                  />
                </div>
              </>
            )}

            {/* Common fields for all types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="1"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active !== false}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Active
              </label>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={handleCloseModal}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default HomepageManagementPage;

